import React from "react";
import type { PrescriptionStats } from "../../services/dashboard.service";

interface Props {
  stats: PrescriptionStats;
  /** Clinic hasn't finished KYC/credentialing yet — same condition CredentialGate
   *  and this page's own "Pending verification" banner already gate prescribing
   *  on, so the numbers aren't meaningful yet. Mirrors RevenueTrendCard/KpiCard's
   *  "Coming soon" treatment. */
  blocked?: boolean;
}

export const PrescriptionsCard: React.FC<Props> = ({ stats, blocked = false }) => {
  const max = Math.max(stats.issued_today, stats.issued_this_week, stats.monthly_average, 1);
  const total = stats.issued_total;

  const rows: { label: string; value: number }[] = [
    { label: "Today",       value: stats.issued_today },
    { label: "This Week",   value: stats.issued_this_week },
    { label: "Monthly Avg", value: Math.round(stats.monthly_average) },
  ];

  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Prescriptions</h3>
        {blocked ? (
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            Coming soon
          </span>
        ) : (
          <span className="text-purple-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
              <path d="m8.5 8.5 7 7" />
            </svg>
          </span>
        )}
      </div>

      <p className="mt-3 text-2xl font-bold text-slate-900">{blocked ? "—" : total.toLocaleString("en-IN")}</p>
      <p className="text-xs text-slate-500">Total issued</p>

      {!blocked && (
        <ul className="mt-4 space-y-3">
          {rows.map((r) => (
            <li key={r.label}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{r.label}</span>
                <span className="font-semibold text-slate-900">{r.value.toLocaleString("en-IN")}</span>
              </div>
              <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-900 rounded-full"
                  style={{ width: `${(r.value / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PrescriptionsCard;
